/**
 * Referral Rewards Context
 *
 * Provides shared state and configuration to referral components
 */
import { createContext, useContext, type FC, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

/**
 * Button text customizations
 */
export interface ButtonTexts {
	share: string;
	join: string;
	signIn: string;
}

/**
 * Referral data from backend
 */
export interface ReferralData {
	referralCode: string;
	referralUrl: string;
	referredContacts: number;
	completedReferrals: number;
}

/**
 * Context value provided to all child components
 */
export interface ReferralContextValue {
	/** Referral data (code, URL, stats) */
	referralData: ReferralData | null;

	/** Whether the current user is a guest */
	isGuest: boolean;

	/** Whether user is enrolled */
	isEnrolled: boolean;

	/** Customizable button texts */
	buttonTexts: ButtonTexts;

	/** Inline styles for buttons from Gutenberg custom colors (optional) */
	buttonInlineStyles?: React.CSSProperties;

	/** Label heading level */
	labelLevel: number;
}

/**
 * Props for the ReferralProvider component
 */
export interface ReferralProviderProps {
	children: ReactNode;
	value: ReferralContextValue;
}

// ============================================================================
// Context
// ============================================================================

const ReferralContext = createContext<ReferralContextValue | undefined>(undefined);

/**
 * Provider component for referral context
 *
 * @example
 * ```tsx
 * <ReferralProvider value={{ referralData, isGuest, isEnrolled, buttonTexts }}>
 *   <ReferralRewardsBlock />
 * </ReferralProvider>
 * ```
 */
export const ReferralProvider: FC<ReferralProviderProps> = ({ children, value }) => {
	return <ReferralContext.Provider value={value}>{children}</ReferralContext.Provider>;
};

/**
 * Hook to access referral context
 *
 * @throws {Error} If used outside of ReferralProvider
 *
 * @example
 * ```tsx
 * const { referralData, isGuest, isEnrolled } = useReferralContext();
 * ```
 */
export function useReferralContext(): ReferralContextValue {
	const context = useContext(ReferralContext);

	if (context === undefined) {
		throw new Error('useReferralContext must be used within a ReferralProvider');
	}

	return context;
}
